export enum UserRole {
  SUPERUSER = 'SUPERUSER',
  ADMIN = 'ADMIN',
  PHARMACY = 'APOTHEEK',
  COURIER = 'KOERIER',
  SUPERVISOR = 'SUPERVISOR',
  PATIENT = 'PATIENT',
  // Deze twee dragen bewust de database-schrijfwijze als waarde, in plaats van
  // de hoofdletterstijl hierboven. Ze zijn later toegevoegd (migratie 014 en
  // 015) en de bestaande waarden veranderen kon niet: ze staan als sleutel in
  // Layout.NAV_ITEMS/ROLE_LABELS en in opgeslagen sessies in localStorage.
  REGION_MANAGER = 'region_manager',
  PLANNER = 'planner'
}

export interface AuthUser {
  id:            string;
  name:          string;
  role:          UserRole;
  pharmacyId?:   string;    // primaire apotheek (backwards compat)
  pharmacyIds?:  string[];  // alle apotheken die deze admin beheert
                            // undefined of leeg = geen beperking (alleen superuser)
  groupId?:      string;    // groep/regio waar een supervisor bij hoort
  // Standplaats uit de planner (planner-migratie 018). Snake_case omdat het veld
  // één-op-één de kolomnaam volgt; de rest van deze interface is camelCase.
  home_pharmacy_id?: string | null;
  courierId?:    string;
  passwordHash?: string;
  // Toegang tot de planner-app, los van de primaire rol (migratie 016). Een
  // sessie die vóór die migratie is opgeslagen heeft het veld niet, dus lees
  // hem altijd als `=== true`.
  isPlanner:     boolean;
}

export interface AuthSession {
  user: AuthUser;
  loggedInAt: string;
}

export enum CourierStatus {
  AVAILABLE = 'BESCHIKBAAR',
  ON_ROUTE = 'ONDERWEG',
  BREAK = 'PAUZE',
  OFFLINE = 'OFFLINE'
}

export enum PackageStatus {
  SCANNING       = 'SCANNING',
  PENDING        = 'PENDING',
  ASSIGNED       = 'ASSIGNED',
  PICKED_UP      = 'PICKED_UP',
  DELIVERED      = 'DELIVERED',
  MAILBOX        = 'MAILBOX',
  NEIGHBOUR      = 'NEIGHBOUR',
  // Patiënt niet thuis, afgehandeld volgens de toelichting van de koerier.
  // Telt NIET als bezorgd: de app weet niet waar het pakket terechtkwam.
  NOT_HOME       = 'NOT_HOME',
  RETURN         = 'RETURN',
  BILLED         = 'BILLED',
  FAILED         = 'FAILED',
  MOVED          = 'MOVED',
  OTHER_LOCATION = 'OTHER_LOCATION',
  REMOVED        = 'REMOVED',
}

export interface Address {
  street: string;
  houseNumber: string;
  postalCode: string;
  city: string;
  lat?: number; // gecached bij inscannen
  lng?: number; // gecached bij inscannen
}

export interface DeliveryEvidence {
  latitude: number;
  longitude: number;
  timestamp: string;
  deliveryNote?: string;
  notHomeOption?: 'mailbox' | 'neighbour' | 'return' | 'moved' | 'other_location' | 'custom';
}

export interface StatusEvent {
  status:    PackageStatus;
  timestamp: string;       // ISO string
  note?:     string;       // bijv. deliveryNote
}

export interface Package {
  id: string;
  pharmacyId: string;
  pharmacyName: string; // Voor financiële rapportage
  address: Address;
  status: PackageStatus;
  courierId?:   string;
  courierName?: string;  // naam van de koerier voor weergave
  createdAt: string;
  // null = expliciet gewist (bijv. bij terugzetten naar de actieve groep).
  // undefined zou door JSON.stringify wegvallen en de kolom in Supabase ongemoeid laten.
  deliveredAt?: string | null;
  deliveryEvidence?: DeliveryEvidence;
  priority: number;
  orderIndex?: number;
  displayIndex?: number; // Backwards compat
  scanNumber?:  number;  // Permanent volgnummer bij scannen (op pakje geschreven)
  routeIndex?:  number;  // Positie in geoptimaliseerde route (kan wijzigen)
  statusHistory?: StatusEvent[];
}

/**
 * Pakket-tellingen van één apotheek, zoals de RPC pharmacy_package_counts ze
 * teruggeeft (migratie 019). Server-side geteld, omdat PostgREST een respons
 * zonder .range() afkapt op 1000 rijen en het overzicht daardoor te laag telde.
 * pharmacyId '' = pakketten zonder (of met een onbekend) apotheek-id.
 */
export interface PharmacyPackageCount {
  pharmacyId: string;
  total:      number;
  delivered:  number;
  pending:    number;
  returned:   number;
  failed:     number;
}

export interface User {
  id: string;
  name: string;
  role: UserRole;
  status?: CourierStatus;
}

export interface Pharmacy {
  id:           string;
  name:         string;
  address?:     string;  // legacy / samengesteld weergave-adres
  street?:      string;
  houseNumber?: string;
  postalCode?:  string;
  city?:        string;
  groupId?:     string;  // groep/regio (verwijst naar groups.id)
  code?:        string;  // optionele interne code / shortcode (vrije tekst)
  courierCode?: string;  // permanente koppelcode voor koeriers, bijv. 'KR-4821'
  hourlyRate?:  number;  // uurtarief dat GoBob factureert (€/uur)
}

// ── Financiële module ───────────────────────────────────────────────
export interface CourierDayStats {
  courierId:    string;
  courierName:  string;
  hourlyWage:   number;
  date:         string;  // 'YYYY-MM-DD'
  firstScan:    string;  // timestamp
  lastDelivery: string;  // timestamp
  lastScan:     string;  // timestamp (laatste scan = max createdAt)
  startTime:    string;  // firstScan - 15 min
  endTime:      string;  // lastScan + 15 min
  totalHours:   number;  // decimaal
  packages:     { pharmacyId: string; count: number }[];
}

export interface PharmacyFinancials {
  pharmacyId:        string;
  pharmacyName:      string;
  hourlyRate:        number;
  period:            string;
  packagesDelivered: number;
  hoursWorked:       number;  // toegewezen uren
  revenue:           number;  // hoursWorked × hourlyRate
  laborCost:         number;  // toegewezen loonkosten
  grossProfit:       number;
  profitMargin:      number;  // %
  revenuePerPackage: number;
  costPerPackage:    number;
  profitPerPackage:  number;
  couriers: {
    name:     string;
    hours:    number;
    wage:     number;
    cost:     number;
    packages: number;
  }[];
}

// Koerier met uurloon (voor het instellen van lonen in het Gebruikers-tabblad)
export interface CourierProfile {
  id:             string;
  name:           string;
  hourlyWage:     number;
  wageStartDate?: string;
}

// ── Vaste instellingen (zorginstellingen die regelmatig bevoorraad worden) ──
export type DeliveryFrequency = 'daily' | 'weekly' | 'both';

export interface Institution {
  id:              string;
  pharmacyId:      string;
  name:            string;
  address:         string;   // samengesteld adres, voor weergave + geocoding
  street?:         string;
  houseNumber?:    string;
  postalCode?:     string;
  city?:           string;
  addressLat?:     number;
  addressLng?:     number;
  frequency:       DeliveryFrequency;
  deliveryDays:    string[]; // 2-letter codes: 'ma','di','wo','do','vr','za','zo'
  instructions?:   string;
  contactPerson?:  string;
  contactPhone?:   string;
  isActive:        boolean;
  createdAt:       string;
}

export interface ChatMessage {
  id:        string;
  role:      'user' | 'assistant';
  text:      string;
  timestamp: string;
}

export interface CallbackRequest {
  phoneNumber:   string;
  preferredTime: string;
  requestedAt:   string;
  isHandled:     boolean;
}

export interface ChatConversation {
  id:               string;
  createdAt:        string;
  expiresAt:        string;         // createdAt + 30 dagen
  pharmacyId:       string;
  messages:         ChatMessage[];
  hasRiskSignal:    boolean;
  callbackRequest?: CallbackRequest;
  isRead:           boolean;
}

export interface ArchiveStats {
  period:        string;
  totalPackages: number;
  delivered:     number;
  mailbox:       number;
  neighbour:     number;
  returned:      number;
  failed:        number;
  moved:         number;   // verhuisd
  otherLocation: number;   // andere locatie
  notHome:       number;   // niet thuis, afgehandeld volgens toelichting
  deliveryRate:  number;   // percentage succesvol bezorgd
  avgPerDay:     number;   // gemiddeld per dag in periode
}

export interface DailyCount {
  date:      string;       // "2026-04-11"
  total:     number;
  delivered: number;
  failed:    number;
}

export interface HeatmapPoint {
  lat:          number;
  lng:          number;
  weight:       number;    // aantal pakketjes op dit adres
  address:      string;
  status:       PackageStatus;
  deliveredAt?: string;    // timestamp van eerste bezorging op dit punt
}
